/**
 * Batched Supabase IN query helper.
 *
 * Splits large arrays of IDs into chunks to avoid exceeding URL length limits
 * (Kong/nginx default ~8KB). Each chunk is queried separately and results merged.
 */
import { supabase } from "@/integrations/supabase/client";

const BATCH_SIZE = 100; // ~100 UUIDs per batch (~3.6KB) stays well under 8KB URL limit

export async function batchedIn<T = any>(
  table: string,
  column: string,
  ids: string[],
  select: string = "*",
  orderBy?: { column: string; ascending: boolean }
): Promise<{ data: T[]; error: any }> {
  if (!ids.length) return { data: [], error: null };

  // Small enough for a single query
  if (ids.length <= BATCH_SIZE) {
    let query = supabase.from(table).select(select).in(column, ids);
    if (orderBy) query = query.order(orderBy.column, { ascending: orderBy.ascending });
    const res = await query;
    return { data: (res.data as T[]) || [], error: res.error };
  }

  // Split into chunks and query in parallel
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    chunks.push(ids.slice(i, i + BATCH_SIZE));
  }

  const results = await Promise.all(
    chunks.map((chunk) => {
      let query = supabase.from(table).select(select).in(column, chunk);
      if (orderBy) query = query.order(orderBy.column, { ascending: orderBy.ascending });
      return query;
    })
  );

  const allData: T[] = [];
  let firstError: any = null;
  for (const res of results) {
    if (res.error && !firstError) firstError = res.error;
    if (res.data) allData.push(...(res.data as T[]));
  }

  return { data: allData, error: firstError };
}
